/**
 * Raw MCP tool names are engineering-flavoured (`list_initiatives`,
 * `list_reference_data`). The chat UI shows the friendlier strings
 * here instead.
 *
 * Keep in sync with the tool registry in `mcp/src/tools/registry.ts`.
 * Adding a new tool to the registry without a row here surfaces in
 * the unit test for this module (test/api/foundry/toolDisplayNames.test.ts)
 * and renders the raw snake_case in the UI until added — gentle
 * reminder, not a hard block.
 */

export interface ToolDisplay {
  /** Short label shown when collapsed (e.g. "Initiatives list"). */
  label: string
  /** Active-voice verb form shown while the tool is running (e.g. "Listing initiatives…"). */
  verb: string
}

export const TOOL_DISPLAY: Record<string, ToolDisplay> = {
  list_initiatives: { label: 'Initiatives list', verb: 'Listing initiatives…' },
  get_initiative: { label: 'Initiative lookup', verb: 'Looking up initiative…' },
  create_initiative: { label: 'Initiative create', verb: 'Creating initiative…' },
  update_initiative: { label: 'Initiative update', verb: 'Updating initiative…' },
  delete_initiative: { label: 'Initiative deactivate', verb: 'Deactivating initiative…' },
  get_gate_readiness: { label: 'Gate readiness', verb: 'Checking gate readiness…' },
  add_comment: { label: 'Comment', verb: 'Posting comment…' },
  list_capabilities: { label: 'Capabilities list', verb: 'Listing capabilities…' },
  get_capability: { label: 'Capability lookup', verb: 'Looking up capability…' },
  list_domains: { label: 'Domains list', verb: 'Listing domains…' },
  get_domain: { label: 'Domain lookup', verb: 'Looking up domain…' },
  list_reference_data: { label: 'Reference data', verb: 'Looking up reference data…' },
}

/**
 * Returns the display strings for a tool name. Falls back to the raw
 * tool name if no mapping exists — surfaces the gap in the UI without
 * crashing.
 */
export function displayForTool(toolName: string): ToolDisplay {
  return TOOL_DISPLAY[toolName] ?? { label: toolName, verb: `Calling ${toolName}…` }
}

/** All tool names that have a registered display. Used by the keep-in-sync test. */
export const KNOWN_TOOL_NAMES = Object.keys(TOOL_DISPLAY).sort()
